import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useEffect } from 'react';
import { POST } from '../../../configs/api';
import { api } from '../../../utils/constants';
import { useDispatch } from 'react-redux';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { addUser } from '../../../store/slices/usersSlice';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import useIsMobile from '../../../hooks/useIsMobile';
import { ASSIGNABLE_ROLES, formatRoleLabel } from '../../../utils/permissions';

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

// Phone formatting function
const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX
  if (phoneNumber.length >= 6) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3, 6);
    const secondPart = phoneNumber.substring(6, 10);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  } else if (phoneNumber.length >= 3) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3);
    return `(${areaCode}) ${firstPart}`;
  } else if (phoneNumber.length > 0) {
    return `(${phoneNumber}`;
  }
  return phoneNumber;
};

const emptyUser = {
  first_name: '',
  last_name: '',
  email: '',
  username: '',
  password: '',
  phone: '',
  roles: ['admin'],
};

const emptyErrors = {
  first_name: '',
  last_name: '',
  email: '',
  username: '',
  password: '',
  phone: '',
  roles: '',
};

export default function AddUserDrawer({ setAddUserDrawerOpen, addUserDrawerOpen, user }) {
  const theme = useTheme();
  const isSmallScreen = useIsMobile();
  const isDarkMode = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const [showError, setShowError] = useState('');
  const [errors, setErrors] = useState(emptyErrors);
  const [newUser, setNewUser] = useState(emptyUser);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Add user api
  const handleAddUser = async () => {
    setLoading(true);
    setShowError('');
    setErrors(emptyErrors);
    let newErrors = {};
    
    // Validation
    (newUser.first_name.length < 2 || newUser.first_name.length > 50) && (newErrors.first_name = 'First name must be between 2 and 50 characters');
    (newUser.last_name.length < 2 || newUser.last_name.length > 50) && (newErrors.last_name = 'Last name must be between 2 and 50 characters');
    
    // Email validation (required for admin/dispatcher)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    (!emailRegex.test(newUser.email)) && (newErrors.email = 'Please enter a valid email address');
    
    // Username validation (required)
    const usernameRegex = /^[a-zA-Z0-9_]+$/;
    if (newUser.username.length < 3 || newUser.username.length > 50) {
      newErrors.username = 'Username must be between 3 and 50 characters';
    } else if (!usernameRegex.test(newUser.username)) {
      newErrors.username = 'Username must contain only alphanumeric characters and underscores';
    }
    
    // Password validation (required for admin/dispatcher)
    (newUser.password.length < 6 || newUser.password.length > 100) && (newErrors.password = 'Password must be between 6 and 100 characters');
    
    // Phone validation (optional for admin/dispatcher)
    if (newUser.phone) {
      const phoneDigits = newUser.phone.replace(/\D/g, '');
      (phoneDigits.length < 10 || phoneDigits.length > 10) && (newErrors.phone = 'Please enter a valid 10-digit US phone number');
    }
    
    (!Array.isArray(newUser.roles) || newUser.roles.length === 0) && (newErrors.roles = 'At least one role is required');
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      const { status, data, message } = await POST(api.createUser, {}, newUser);
      if (status === 201) {
        setShowSuccessToast(true);
        setNewUser(emptyUser);
        dispatch(addUser(data));
      } else if (status === 409) {
        setShowError(message || 'A user with this email already exists.');
      } else {
        setShowError(data?.message || 'Something went wrong, please try again later.');
      }
    }
    setLoading(false);
  };

  // Clear form errors
  useEffect(() => {
    setErrors(emptyErrors);
    setShowError('');
  }, [newUser]);

  return (
    <Dialog
      open={addUserDrawerOpen}
      onClose={() => setAddUserDrawerOpen(false)}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      PaperComponent={isSmallScreen ? undefined : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: isSmallScreen ? 0 : 2,
            width: isSmallScreen ? '100%' : '40vw',
            height: isSmallScreen ? '100%' : '75vh',
            maxWidth: isSmallScreen ? '100%' : 'none',
            maxHeight: isSmallScreen ? '100%' : 'none',
            m: isSmallScreen ? 0 : undefined,
          }
        }
      }}
    >
      {/* Header */}
      <Box 
        id="draggable-dialog-title"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          p: { xs: 2, md: 3 },
          backgroundColor: 'background.paper',
          cursor: isSmallScreen ? 'default' : 'move'
        }}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 700, 
          color: 'text.primary',
          fontSize: '1.25rem',
          fontFamily: 'inherit'
        }}>
          Add User
        </Typography>
        <IconButton 
          onClick={() => setAddUserDrawerOpen(false)} 
          sx={{ 
            color: 'text.secondary',
            padding: '4px',
            '&:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Error Message */}
        {showError && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 600 }}>
              {showError}
            </Typography>
          </Box>
        )}

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* First Name */}
          <TextField
            fullWidth
            label="First Name"
            value={newUser.first_name || ''}
            onChange={(event) => setNewUser({ ...newUser, first_name: event.target.value })}
            error={!!errors.first_name}
            helperText={errors.first_name}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Last Name */}
          <TextField
            fullWidth
            label="Last Name"
            value={newUser.last_name || ''}
            onChange={(event) => setNewUser({ ...newUser, last_name: event.target.value })}
            error={!!errors.last_name}
            helperText={errors.last_name}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

          {/* Email */}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={newUser.email || ''}
            onChange={(event) => setNewUser({ ...newUser, email: event.target.value })}
            error={!!errors.email}
            helperText={errors.email}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
            slotProps={{
              input: {
                autoComplete: 'new-email'
              }
            }}
          />

          {/* Username */}
          <TextField
            fullWidth
            label="Username"
            value={newUser.username || ''}
            onChange={(event) => setNewUser({ ...newUser, username: event.target.value })}
            error={!!errors.username}
            helperText={errors.username || '3-50 characters, alphanumeric and underscores only'}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
            slotProps={{
              input: {
                autoComplete: 'username'
              }
            }}
          />

          {/* Password */}
          <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={newUser.password || ''}
              onChange={(event) => setNewUser({ ...newUser, password: event.target.value })}
              error={!!errors.password}
              helperText={errors.password || 'Minimum 6 characters'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                      sx={{
                        color: 'text.secondary',
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                        },
                        '& svg': {
                          fontSize: '1.25rem',
                        }
                      }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
                inputProps: {
                  autoComplete: 'new-password'
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  '& fieldset': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  '&.Mui-focused': {
                    color: isDarkMode ? '#fff' : '#000',
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  color: 'text.primary',
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '0.75rem',
                }
              }}
            />

          {/* Role */}
          <FormControl fullWidth error={!!errors.roles}>
            <InputLabel sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'text.secondary',
              '&.Mui-focused': {
                color: isDarkMode ? '#fff' : '#000',
              }
            }}>Roles</InputLabel>
            <Select
              multiple
              value={Array.isArray(newUser.roles) ? newUser.roles : []}
              label="Roles"
              onChange={(event) => {
                const value = event.target.value;
                setNewUser({ ...newUser, roles: typeof value === 'string' ? value.split(',') : value });
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={formatRoleLabel(value)} size="small" sx={{ height: 22, fontSize: '0.75rem' }} />
                  ))}
                </Box>
              )}
              sx={{
                borderRadius: 1,
                backgroundColor: 'background.paper',
                fontSize: '0.875rem',
                color: 'text.primary',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              }}
            >
              {ASSIGNABLE_ROLES.map((role) => (
                <MenuItem key={role} value={role} sx={{ fontSize: '0.875rem' }}>
                  <Checkbox checked={(newUser.roles || []).includes(role)} size="small" />
                  <ListItemText primary={formatRoleLabel(role)} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
              ))}
            </Select>
            {errors.roles && (
              <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 1.5, fontSize: '0.75rem' }}>
                {errors.roles}
              </Typography>
            )}
          </FormControl>

          {/* Phone */}
          <TextField
            fullWidth
            label="Phone (Optional)"
            value={newUser.phone || ''}
            onChange={(event) => {
              const formattedPhone = formatPhoneNumber(event.target.value);
              setNewUser({ ...newUser, phone: formattedPhone });
            }}
            error={!!errors.phone}
            helperText={errors.phone}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
                    <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>
                      +1
                    </Typography>
                  </Box>
                </InputAdornment>
              ),
            }}
            placeholder="(555) 123-4567"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                color: 'text.primary',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
              }
            }}
          />

        </Box>
      </DialogContent>

      {/* Dialog Actions */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        gap: 2,
        p: 3, 
        borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0' 
      }}>
        <Button 
          onClick={() => setAddUserDrawerOpen(false)}
          variant="outlined"
          sx={{ 
            borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            color: 'text.primary',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            borderRadius: 1,
            '&:hover': {
              borderColor: isDarkMode ? '#475569' : '#bdbdbd',
              backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
            }
          }}
        >
          Cancel
        </Button>
        
        <Button 
          variant="contained"
          disabled={loading}
          onClick={handleAddUser}
          sx={{ 
            backgroundColor: isDarkMode ? '#fff' : '#000',
            color: isDarkMode ? '#000' : 'white',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            borderRadius: 1,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: isDarkMode ? '#475569' : '#666',
              color: isDarkMode ? '#94a3b8' : '#fff',
            }
          }}
        >
          {loading ? 'Adding...' : 'Add User'}
        </Button>
      </Box>

      {/* Success Toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          User added successfully
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

